<!-- eslint-disable -->

<template>
  <div>
    <DataTable
      :value="quoteRows"
      :rows="10"
      stripedRows
      responsive-layout="stack"
      :rowClass="getRowClass"
      :loading="loading"
    >
      <template #empty>
        <p class="text-center">No quotes found.</p>
      </template>
      <Column field="name" header="Quote">
        <template #body="{ data }">
          <div class="quote-row-flex">
            <span v-if="data.type === 'root' && hasChildren(data.quote)" class="chevron" @click.stop="toggleExpandRow(data.quote.id)">
              <svg :class="{ expanded: isRowExpanded(data.quote.id) }" width="16" height="16" viewBox="0 0 16 16">
                <polyline points="6,4 10,8 6,12" fill="none" stroke="currentColor" stroke-width="2" />
              </svg>
            </span>
            <span :class="data.type === 'child' ? 'child-quote-cell' : ''">
              <div class="quote-name-container">
                <span class="quote-main-row">
                  <span v-if="data.type === 'root' && data.quote.version_number && data.quote.version_number !== 1" class="version-number">V{{ data.quote.version_number }}</span>
                  <span v-if="data.type === 'child' && data.quote.version_number && data.quote.version_number !== 1" class="version-number">V{{ data.quote.version_number }}</span>
                  <router-link :to="{ name: 'EditQuote', params: { quoteId: data.quote.id } }" class="quote-link">
                    {{ data.quote?.name || '(pending)' }}  <!-- Pending is for Copy Quotes with a pending (null) quote name -->
                  </router-link>
                  <span v-if="data.type === 'child'" class="quote-relationship-indicator" title="Child Quote">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                    </svg>
                  </span>
                </span>
                <span v-if="data.type === 'root' && Array.isArray(data.quote.children) && data.quote.children.length > 0" class="quote-version-muted">
                  ({{ data.quote.children.length + 1 }} versions)
                </span>
              </div>
            </span>
          </div>
        </template>
      </Column>
      <Column field="erp_customer_name" header="Customer">
        <template #body="{ data }">
          {{ data.quote?.erp_customer_name }}
        </template>
      </Column>
      <Column field="erp_well_name" header="Well">
        <!--  Dev note: retain double whitespaces in column displays (ideally).
        Some quotes were saved with SK data containing double whitespaces.
        If users copy/paste, those whitespaces will be needed for some GQL
        operators such as _ilike -->
        <template #body="{ data }">
          <div class="retain-sk-double-whitespaces">
            {{ data.quote?.erp_well_name }}
          </div>
        </template>
      </Column>
      <Column field="erp_address_name" header="Address">
        <template #body="{ data }">
          <div class="retain-sk-double-whitespaces">
            {{ data.quote?.erp_address_name }}
          </div>
        </template>
      </Column>
      <Column field="erp_plant_name" header="Sales District">
        <template #body="{ data }">
          {{ data.quote?.erp_plant_name }}
        </template>
      </Column>
      <Column field="created_at" header="Created" class="quote-no-wrap">
        <template #body="{ data }">
          <span class="quote-no-wrap">
            {{ data.quote?.created_at ? getSpyGlassDate(data.quote?.created_at) : '' }}
          </span>
        </template>
      </Column>
      <Column field="pricing_date" header="Pricing Date">
        <template #body="{ data }">
          <span class="quote-no-wrap">
            {{ data.quote?.pricing_date ? getSpyGlassDate(data.quote?.pricing_date) : '' }}
          </span>
        </template>
      </Column>
      <Column field="sk_job_number" header="SK Job">
        <template #body="{ data }">
          {{ data.quote?.sk_job_number }}
        </template>
      </Column>
      <Column field="status" header="Status">
        <template #body="{ data }">
          <span class="capitalize">
            {{ data.quote?.status }}
          </span>
        </template>
      </Column>
      <Column field="account_manager_display_name" header="Account Manager">
        <template #body="{ data }">
          {{ data.quote.user?.display_name }}
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable */

import { ref, computed } from "vue";
import { type Quotes } from "gql/api";
import { getSpyGlassDate, hasChildren, QuoteWithChildren } from "@/utils/quoteHelpers";

enum RELATIONSHIP {
  root = 'root',
  child = 'child'
}

type QuoteRow = {
  type: RELATIONSHIP,
  quote: QuoteWithChildren,
  parent?: QuoteWithChildren,
  children?: QuoteWithChildren[]
}

const props = defineProps<{
  quotes: Quotes[] | undefined;
  groupedMode?: boolean;
  loading: boolean;
}>();

const expanded = ref<Record<string, boolean>>({});

const toggleExpandRow = (quoteId: string) => {
  expanded.value[quoteId] = !expanded.value[quoteId];
};

const isRowExpanded = (quoteId: string) => !!expanded.value[quoteId];

/**
 * Flattens a grouped/filtered quotes array into a display-ready flat array.
 * Each item is annotated as 'root' or 'child', with parent/children references as needed.
 * Used to drive table rendering logic.
 */
const flattenQuotesForDisplay = (quotes: QuoteWithChildren[], expanded: Record<string, boolean>): QuoteRow[] => {
  const rootRows: QuoteRow[] = [];

  for (const rootQuote of quotes) {
    // Always treat as root (pure or dirty)
    rootRows.push({ type: RELATIONSHIP.root, quote: rootQuote, children: rootQuote.children });
    
    const isRowExpanded = expanded[rootQuote.id];
    const isRowContainingChildren = rootQuote.children?.length;
    const isRowValidToAddChildrenRows = isRowExpanded && isRowContainingChildren;
    if (isRowValidToAddChildrenRows) {
      rootQuote.children?.forEach((childQuote) => {
        // Finally, add the children rows to the rootQuote:
        const childRow = { type: RELATIONSHIP.child, quote: childQuote, parent: rootQuote }
        rootRows.push(childRow);
      })
    }
  }

  return rootRows;
};

const quoteRows = computed(() => {
  return flattenQuotesForDisplay(props.quotes || [], expanded.value);
});

const getRowClass = (row: QuoteRow) => {
  return row.type === RELATIONSHIP.child ? 'child-row' : ''
};
</script>

<style lang="scss" scoped>
.quote-row-flex {
  display: flex;
  align-items: center;
  padding-left: 0;
}
.chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  vertical-align: middle;
  cursor: pointer;
  margin-right: 4px;
  flex-shrink: 0;
  
  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.2s;
    color: #b0b0b0;
    flex-shrink: 0;
  }
  
  .expanded {
    transform: rotate(90deg);
  }
}
.child-row {
  font-size: 0.90em;
  color: #b0b0b0;
  background: #23292d !important;
  td {
    padding-top: 0.5em !important;
    padding-bottom: 0.5em !important;
  }
}
.child-quote-cell {
  padding-left: 2.5em;
  display: inline-block;
}
.quote-no-wrap {
  white-space: nowrap !important;
}
.quote-version-muted {
  color: var(--gray-500, #b0b0b0);
  font-size: 0.95em;
  font-style: italic;
  margin-left: 0.5em;
  pointer-events: none;
  user-select: none;
}
.quote-relationship-indicator {
  display: inline-flex;
  align-items: center;
  margin-left: 0.5em;
  color: var(--gray-500);
  vertical-align: middle;
  
  svg {
    width: 14px;
    height: 14px;
  }
  
  &:hover {
    color: var(--gray-300);
  }
}
.version-number {
  margin-right: 0.5em;
  color: var(--gray-500);
  font-weight: 500;
  display: inline-flex;
  align-items: center;
}
.quote-name-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  
  .quote-main-row {
    display: flex;
    align-items: center;
    line-height: 1;
  }
  
  .quote-link {
    display: block;
    margin-top: 0;
    line-height: 1;
  }
  
  .quote-version-muted {
    margin-top: 0.1em;
    margin-left: 0;
  }
}
.retain-sk-double-whitespaces {
  white-space: pre-wrap;
}
</style>